// src/components/Trans/Trans.jsx
import PropTypes from 'prop-types';
import { useI18n } from '../../context/I18nContext';

/**
 * Trans component for handling translations with styled components
 * Similar to react-i18next's Trans component but works with our custom i18n system
 * 
 * Usage:
 * <Trans
 *   i18nKey="ConversationDetailsView.xPeopleInConversation"
 *   defaults="<0>{numPar}</0> People in this conversation"
 *   components={[<span style={{color: 'red'}} />]}
 *   values={{numPar: 5}}
 * />
 */
const Trans = ({
  i18nKey,
  defaults,
  components = [],
  values = {},
  count,
}) => {
  const { t } = useI18n();
  
  // Get the raw translated string (without variable interpolation yet)
  let translatedString = t(i18nKey, {}, defaults);
  
  if (typeof translatedString !== 'string') {
    return translatedString;
  }
  
  // First, handle variable interpolation for {var} patterns
  translatedString = translatedString.replace(/\{(\w+)\}/g, (match, variable) => {
    if (Object.prototype.hasOwnProperty.call(values, variable)) {
      return values[variable];
    }
    return match;
  });
  
  // Handle pluralization
  translatedString = translatedString.replace(
    /\{(\w+), plural, =1 \{([^}]+)\} other \{([^}]+)\}\}/g,
    (match, variable, singular, plural) => {
      const value = values[variable] || count;
      return value === 1 ? singular : plural;
    },
  );
  
  if (typeof translatedString !== 'string' || components.length === 0) {
    return translatedString;
  }
  
  // Parse the string and replace <0>, <1>, etc. with components
  const parts = [];
  let currentIndex = 0;
  
  // Regular expression to match component tags like <0>, <1>, etc.
  const componentRegex = /<(\d+)>(.*?)<\/\1>|<(\d+)>/g;
  let match;
  
  while ((match = componentRegex.exec(translatedString)) !== null) {
    // Add text before the component
    if (match.index > currentIndex) {
      parts.push(translatedString.slice(currentIndex, match.index));
    }
    
    const componentIndex = parseInt(match[1] || match[3], 10);
    const innerContent = match[2]; // Content between opening and closing tags
    
    if (components[componentIndex]) {
      // Clone the component and add the inner content
      const component = components[componentIndex];
      if (innerContent) {
        // Self-closing tag with content: <0>content</0>
        parts.push({
          ...component,
          key: parts.length,
          props: {
            ...component.props,
            children: innerContent,
          },
        });
      } else {
        // Self-closing tag: <0/>
        parts.push({
          ...component,
          key: parts.length,
        });
      }
    } else {
      // Component not found, keep original text
      parts.push(match[0]);
    }
    
    currentIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (currentIndex < translatedString.length) {
    parts.push(translatedString.slice(currentIndex));
  }
  
  // If no components were found, return the string as-is
  if (parts.length === 0) {
    return translatedString;
  }
  
  return <>{parts}</>;
};

Trans.propTypes = {
  i18nKey: PropTypes.string.isRequired,
  defaults: PropTypes.string,
  components: PropTypes.arrayOf(PropTypes.element),
  values: PropTypes.object,
  count: PropTypes.number,
};

export default Trans;
